import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  ArrowUpRight,
  Wallet,
  Briefcase,
  Building2,
  Home as HomeIcon,
  ShieldCheck,
  LineChart,
  Stethoscope,
  TrendingUp,
  Check,
  FileCheck2,
  ArrowLeftRight,
  FileSignature,
  ScrollText,
  Compass,
  BadgeCheck,
} from "lucide-react";
import { services, additionalServices } from "../lib/data";
import useSEO from "../lib/seo";

const iconMap = {
  Wallet,
  Briefcase,
  Building2,
  Home: HomeIcon,
  ShieldCheck,
  LineChart,
  Stethoscope,
  TrendingUp,
};

const addOnIconMap = {
  FileCheck2,
  ArrowLeftRight,
  FileSignature,
  ScrollText,
  Compass,
  BadgeCheck,
};

const bullets = {
  "personal-loans": [
    "Unsecured lending with competitive pricing",
    "Transparent fees and quick decisioning",
    "Balance transfer and top-up options",
  ],
  "business-loans": [
    "Working capital, term and unsecured options",
    "Structured for MSMEs and growing enterprises",
    "Assistance with documentation and lender prep",
  ],
  "mortgage-loans": [
    "Loan against residential or commercial property",
    "Led by ₹150+ Cr housing & LAP disbursement experience",
    "Clear process from valuation to disbursal",
  ],
  "home-loans": [
    "First-time buyer and balance transfer options",
    "Multiple lender comparisons in one place",
    "Assistance on eligibility and documentation",
  ],
  "doctor-professional-loans": [
    "Preferential rates for doctors & qualified professionals",
    "Funding for clinic set-up, equipment and working capital",
    "Personal loan options for professional needs",
  ],
  insurance: [
    "Life, health, vehicle, motor and general insurance",
    "Partnerships with leading insurers",
    "Claims support and renewal reminders",
  ],
  investments: [
    "Goal-based mutual fund recommendations",
    "Systematic Investment Plans (SIPs)",
    "Backed by 11+ years of advisory experience",
  ],
  "sme-corporate": [
    "Structured capital support for SMEs",
    "Credit and operational resources for corporates",
    "End-to-end compliant and audit-ready",
  ],
};

export default function Services() {
  useSEO({
    title:
      "Services — Personal, Home, Business, Mortgage, Doctor Loans, Insurance & SIPs",
    description:
      "Eight core financial services from DVP DIGI WORLD: Personal Loans, Home Loans, Business Loans, Mortgage (LAP), Doctor & Professional Loans, Insurance, Mutual Funds & SIPs, plus SME & Corporate solutions.",
    path: "/services",
  });
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div data-testid="page-services">
      {/* HERO */}
      <section className="relative border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1d4ed8]/20 via-transparent to-[#dc2626]/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#dc2626]">
            What We Offer
          </div>
          <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-7xl tracking-tighter text-white max-w-4xl leading-[0.95]">
            Solutions across <span className="text-[#1d4ed8]">credit</span>,{" "}
            <span className="text-[#dc2626]">protection</span> &amp; structured finance.
          </h1>
          <p className="mt-6 text-lg text-neutral-300 max-w-3xl leading-relaxed">
            We serve a wide customer base with solutions tailored to retail, SME
            and corporate requirements. Explore our six core product categories
            below.
          </p>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-24">
          {services.map((s, i) => {
            const Icon = iconMap[s.icon] || ShieldCheck;
            const isEven = i % 2 === 0;
            return (
              <div
                key={s.slug}
                id={s.slug}
                data-testid={`service-section-${s.slug}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  isEven ? "" : "lg:[&>*:first-child]:order-2"
                }`}
              >
                <div className="lg:col-span-5">
                  <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 bg-[#121212] p-10">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#dc2626]/10 via-transparent to-[#1d4ed8]/10" />
                    <div className="relative h-full w-full flex flex-col justify-between">
                      <div>
                        <div className="font-mono text-xs text-neutral-500 tracking-[0.25em] uppercase">
                          Product / 0{i + 1}
                        </div>
                      </div>
                      <div className="flex items-end justify-between">
                        <Icon className="h-24 w-24 text-[#dc2626] opacity-90" strokeWidth={1.2} />
                        <div className="font-display font-bold text-5xl text-white/10">
                          0{i + 1}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#1d4ed8]">
                    {s.title}
                  </div>
                  <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white">
                    {s.short}
                  </h2>
                  <p className="mt-5 text-neutral-300 leading-relaxed text-lg">
                    {s.long}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {(bullets[s.slug] || []).map((b, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-3 text-neutral-300"
                      >
                        <span className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-[#dc2626]/15 text-[#dc2626] flex items-center justify-center">
                          <Check className="h-3 w-3" />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/request-access"
                    data-testid={`service-cta-${s.slug}`}
                    className="mt-8 inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white px-5 py-3 font-semibold"
                  >
                    Apply for {s.title} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ADDITIONAL SERVICES */}
      <section className="py-14 sm:py-20 lg:py-28 border-t border-white/5 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#1d4ed8]">
              Beyond Finance
            </div>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white">
              Additional services for a{" "}
              <span className="text-[#dc2626]">hassle-free</span> experience.
            </h2>
            <p className="mt-5 text-neutral-400 leading-relaxed text-lg">
              Our services extend beyond finance — we handle the paperwork,
              approvals and coordination so you can focus on what matters.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalServices.map((s, i) => {
              const Icon = addOnIconMap[s.icon] || Check;
              return (
                <div
                  key={i}
                  data-testid={`addon-service-${i}`}
                  className="group bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-white/20 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="h-11 w-11 rounded-lg bg-[#1d4ed8]/10 border border-[#1d4ed8]/20 flex items-center justify-center text-[#1d4ed8] mb-4 group-hover:bg-[#1d4ed8] group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display font-semibold text-lg text-white mb-2">
                    {s.title}
                  </h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
